import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuthStore } from '../stores/authStore';

interface BrandingSettings {
  logo: string;
  favicon: string;
  adminLogo: string;
  sidebarIcon: string;
  companyName: string;
  tagline: string;
  primaryColor: string;
  secondaryColor: string;
  accentColor: string;
  loginTitle: string;
  loginSubtitle: string;
  copyrightText: string;
  supportEmail: string;
  showPoweredBy: boolean;
  loginBackgroundTheme?: string;
  loginBackgroundImage?: string;
}

// Default CruvzMail branding (dark navy theme)
const defaultBranding: BrandingSettings = {
  logo: '',
  favicon: '',
  adminLogo: '',
  sidebarIcon: '',
  companyName: '',
  tagline: 'Professional Email Service',
  primaryColor: '#0a1929',
  secondaryColor: '#0d2137',
  accentColor: '#64b5f6',
  loginTitle: 'Sign in to your account',
  loginSubtitle: 'Sign in to access your mailbox',
  copyrightText: `© ${new Date().getFullYear()} CruvzMail. All rights reserved.`,
  supportEmail: 'support@cruvzmail.com',
  showPoweredBy: true,
  loginBackgroundTheme: '1',
  loginBackgroundImage: '',
};

// Helper function to update document favicon dynamically
const updateFavicon = (faviconUrl: string) => {
  if (!faviconUrl) return;
  
  // Remove existing favicon links
  const existingLinks = document.querySelectorAll("link[rel*='icon']");
  existingLinks.forEach(link => link.remove());
  
  // Create new favicon link
  const link = document.createElement('link');
  link.rel = 'icon';
  link.type = faviconUrl.endsWith('.svg') ? 'image/svg+xml' : 
              faviconUrl.endsWith('.png') ? 'image/png' : 'image/x-icon';
  link.href = faviconUrl;
  document.head.appendChild(link);
  
  // Also add apple-touch-icon for mobile
  const appleLink = document.createElement('link');
  appleLink.rel = 'apple-touch-icon';
  appleLink.href = faviconUrl;
  document.head.appendChild(appleLink);
};

// Helper function to update document title
const updateTitle = (companyName: string) => {
  // Only update if we have a custom company name and current title contains default
  if (companyName && companyName !== 'CruvzMail') {
    const currentTitle = document.title;
    if (currentTitle.includes('CruvzMail') || currentTitle.includes('ZMail')) {
      document.title = currentTitle.replace(/CruvzMail|ZMail/g, companyName);
    }
  }
};

interface BrandingContextType {
  branding: BrandingSettings;
  loading: boolean;
  currentDomain: string | null;
  setDomain: (domain: string) => void;
  refetch: () => Promise<void>;
}

const BrandingContext = createContext<BrandingContextType | null>(null);

// Cache helpers
const CACHE_PREFIX = 'cruvz_branding_';
const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

const getCachedBranding = (domain: string): BrandingSettings | null => {
  try {
    const raw = localStorage.getItem(CACHE_PREFIX + domain);
    if (!raw) return null;
    const { data, ts } = JSON.parse(raw);
    if (Date.now() - ts > CACHE_TTL_MS) { localStorage.removeItem(CACHE_PREFIX + domain); return null; }
    return data as BrandingSettings;
  } catch { return null; }
};

const setCachedBranding = (domain: string, data: BrandingSettings) => {
  try { localStorage.setItem(CACHE_PREFIX + domain, JSON.stringify({ data, ts: Date.now() })); } catch {}
};

export const BrandingProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Hydrate from cache immediately to avoid flash of default branding on refresh
  const getInitialBranding = (): BrandingSettings => {
    const host = window.location.hostname;
    if (host && host !== 'localhost') {
      const cached = getCachedBranding(host);
      if (cached) return cached;
    }
    return defaultBranding;
  };

  const [branding, setBranding] = useState<BrandingSettings>(getInitialBranding);
  const [loading, setLoading] = useState(false);
  const [currentDomain, setCurrentDomain] = useState<string | null>(null);

  const apiUrl = import.meta.env.VITE_API_URL || '/api';

  // Version counter to prevent stale responses from overwriting newer ones
  const fetchVersion = React.useRef(0);

  const fetchBranding = useCallback(async (domain?: string) => {
    const version = ++fetchVersion.current;
    setLoading(true);
    try {
      // Fetch public branding (no auth required)
      const params = domain ? { domain } : {};
      const response = await axios.get(`${apiUrl}/branding/public`, { params });
      // If a newer fetch was started while this one was in-flight, discard this result
      if (version !== fetchVersion.current) return;
      const data = response.data?.data || response.data;
      
      if (data && Object.keys(data).length > 0) {
        const newBranding = { ...defaultBranding, ...data };
        setBranding(newBranding);
        // Cache for next load so there's no flash on refresh
        if (domain) setCachedBranding(domain, newBranding);
        
        // Update favicon and title dynamically
        if (newBranding.favicon) {
          updateFavicon(newBranding.favicon);
        }
        if (newBranding.companyName) {
          updateTitle(newBranding.companyName);
        }
      } else {
        // No custom branding for this domain, use default
        setBranding(defaultBranding);
      }
    } catch (err) {
      if (version !== fetchVersion.current) return;
      console.error('Failed to fetch branding:', err);
      // On error, keep cached/current branding rather than flashing to default
    } finally {
      if (version === fetchVersion.current) setLoading(false);
    }
  }, [apiUrl]);

  // Also update favicon/title when branding changes
  useEffect(() => {
    if (branding.favicon) {
      updateFavicon(branding.favicon);
    }
    if (branding.companyName) {
      updateTitle(branding.companyName);
    }
  }, [branding.favicon, branding.companyName]);

  // Determine the correct domain to fetch branding for.
  // On mobile/Capacitor, hostname is cruvzmail.com but user may be from another domain.
  // The user's email domain takes priority over hostname.
  const user = useAuthStore((s) => s.user);
  useEffect(() => {
    const host = window.location.hostname;
    // If the user is logged in, use their email domain for branding
    if (user?.email) {
      const emailDomain = user.email.split('@')[1];
      if (emailDomain) {
        // If we have a cache for the user's domain, apply it immediately (no flash)
        if (emailDomain !== currentDomain) {
          const cached = getCachedBranding(emailDomain);
          if (cached) setBranding(cached);
          setCurrentDomain(emailDomain);
          fetchBranding(emailDomain);
        }
        return;
      }
    }
    // No user logged in — use hostname
    if (host && host !== 'localhost') {
      fetchBranding(host);
    } else {
      fetchBranding();
    }
  }, [user?.email, fetchBranding]);

  // Function to set domain and fetch domain-specific branding
  const setDomain = useCallback((domain: string) => {
    if (domain && domain !== currentDomain) {
      setCurrentDomain(domain);
      fetchBranding(domain);
    }
  }, [currentDomain, fetchBranding]);

  const refetch = useCallback(async () => {
    await fetchBranding(currentDomain || undefined);
  }, [fetchBranding, currentDomain]);

  return (
    <BrandingContext.Provider value={{ branding, loading, currentDomain, setDomain, refetch }}>
      {children}
    </BrandingContext.Provider>
  );
};

export const useBranding = () => {
  const context = useContext(BrandingContext);
  if (!context) {
    throw new Error('useBranding must be used within a BrandingProvider');
  }
  return context;
};

export default BrandingContext;
